/* eslint-disable */
// src/layouts/localMachine/index.js

import React, { useEffect, useState, useMemo,useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import LocalService from "services/localService";
import LocalMachineService from "services/localMachineService";
import LocalMachineProfitPercentageService from "services/localMachineProfitPercentageService";
import LocalMachineCounterConfigService from "services/localMachineCounterConfigService";
import CounterFormulaService from "services/counterFormulaService";
import VisitMachineService from "services/visitMachineService";

import {
  Grid, Card, Button, List, ListItem, ListItemText, IconButton, Box, TextField,
  MenuItem, Autocomplete, FormControlLabel, Checkbox, Accordion, AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import Alert from "@mui/material/Alert";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import esLocale from "date-fns/locale/es";
import AppSnackbar from "components/common/AppSnackbar";
import AppActionDialog from "components/common/AppActionDialog";
import AppFormDialog from "components/common/AppFormDialog";
import AppFormContext from "components/common/AppFormContext";
import GlobalStyles from "@mui/material/GlobalStyles";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SpeedIcon from "@mui/icons-material/Speed";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditLocalMachineForm from "forms/localMachine/editLocalMachineForm";
import EditLocalDisplayNumberForm from "forms/localMachine/editLocalDisplayNumberForm";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import CreateLocalMachineForm from "forms/localMachine/createLocalMachineForm";
import DeleteLocalMachineForm from "forms/localMachine/deleteLocalMachineForm";


import AddVisitTransactionButton from "components/VisitTransactionDetails/addVisitTransactionButton";
import { machineTypeLabel, machineTypeGender } from "utils/machineLabels";
import { moneyFmtDynamic, nfDynamic } from "utils/formatters";
import { sortLocalMachinesFixed, sortTypeEntries } from "utils/localMachineOrder";
import { buildMachineDisplayName } from "utils/machineDisplayName";
import { formatFormulaLabel } from "utils/formula";
import { getAccumulationModeLabel } from "utils/accumulationModeLabels";

import { hasRent, getRentPrice, getRentFrequency } from "utils/rent";

import {
  TypeBlocksGlobalStyles, TypeBlock, TypeChipsHeader,
  MachineName, MachineMeta,
} from "ui/skins/typeBlocks";
import BulkMoveLocalMachinesForm from "forms/localMachine/bulkMoveLocalMachinesForm";
import CancelButton from "components/common/CancelButton";
import AcceptButton from "components/common/AcceptButton";
import { secondaryOutlinedButtonSx } from "components/common/buttonStyles";
import AppNumberInput from "components/common/AppNumberInput";

import EditFormulasByTypeForm from "forms/localMachine/EditFormulasByTypeForm";
import EditProfitPercentagesForm from "forms/localMachine/EditProfitPercentagesForm";
import InitialValuesPerMachineForm from "forms/localMachine/InitialValuesPerMachineForm";
import CounterInitialValuesForm from "forms/localMachine/CounterInitialValuesForm";
import { getCurrentRole } from "utils/auth";
import { displayOptionalPersonField } from "utils/personOptionalFields";
import { parseDateOnlyToLocalDate, toLocalDateOnlyString } from "utils/operationDate";

const money = (n) => moneyFmtDynamic.format(n);
const number = (n) => nfDynamic.format(n);
const CUSTOM_COUNTER_FORMULA_OPTION = { Id: "__custom__", Type: "", Value: "", isCustom: true };
const NO_COUNTER_FORMULA_OPTION = { Id: "__none__", Type: "", Value: "", isNone: true };

// Helpers de tipo
// Helpers de tipo (agrupado vs skin)
  const KNOWN_TYPES = new Set(["Rockola", "Pool", "Tejo", "Peluchera", "Tragamoneda"]);

  // etiqueta visible (lo que viene de la BD o "No asignado")
const getTypeLabel = (m) => {
  const raw =
    m?.MachineType?.Type ??
    m?.MachineType ??
    m?.Machine?.MachineType?.Type ??
    m?.Machine?.MachineType ??
    "No asignado";

  return String(raw).trim();
};

  // clave para el skin (si no es conocido → Default)
  const getTypeKey = (label) => (KNOWN_TYPES.has(label) ? label : "Default");

  // compatibilidad con el resto del archivo
  const getType = (m) => getTypeLabel(m); // para la lógica de columnas/infos

  const isFichaType = (t) => t === "Pool" || t === "Tejo" || t === "Peluchera";
  const isTraga = (t) => t === "Tragamoneda"; // tu BD usa "Tragamoneda" exacto

// Tomar valor/nombre de fórmula de las rutas más comunes
const getFormulaValue = (m) => {
  const raw =
    m?.Formula?.Value ??
    m?.Machine?.Formula?.Value ??
    m?.MachineFormula?.Value ??
    null;
  const n = raw != null ? Number(raw) : null;
  return Number.isFinite(n) ? n : null;
};
const getFormulaName = (m) => formatFormulaLabel(m, { fallback: null });

const normalizeLocalType = (type) =>
  String(type || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

const getTodayDateOnlyValue = () => {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

const isValidDateOnlyValue = (value) => /^\d{4}-\d{2}-\d{2}$/.test(String(value || ""));

const isFutureDateOnlyValue = (value) => {
  if (!isValidDateOnlyValue(value)) return false;
  return value > getTodayDateOnlyValue();
};

const clampDateOnlyValue = (value, minValue, maxValue) => {
  if (!isValidDateOnlyValue(value)) return value;
  let nextValue = value;
  if (isValidDateOnlyValue(minValue) && nextValue < minValue) nextValue = minValue;
  if (isValidDateOnlyValue(maxValue) && nextValue > maxValue) nextValue = maxValue;
  return nextValue;
};

const formatDateOnlyForDisplay = (value) => {
  if (!isValidDateOnlyValue(value)) return "-";
  const [year, month, day] = String(value).split("-").map(Number);
  return new Date(year, month - 1, day).toLocaleDateString("es-UY");
};

const getCounterConfigDateBoundsMessage = (bounds) => {
  switch (bounds?.ReferenceKind) {
    case "last_collection":
      return "La fecha no puede ser anterior a la última recaudación registrada para esta máquina.";
    case "active_config_start":
      return "La fecha no puede ser anterior a la configuración activa actual.";
    case "last_counter_config_finish":
      return "La fecha no puede ser anterior al cierre del ciclo de contadores anterior.";
    case "local_machine_start":
      return "La fecha no puede ser anterior al ingreso de la máquina al local.";
    default:
      return "La fecha efectiva no es válida.";
  }
};

const getCounterConfigDateBoundsHelperText = (bounds, isLoading) => {
  if (isLoading) return "Cargando limites de fecha...";
  if (!bounds?.MinEffectiveDate) return "";
  const nextCollectionText = bounds?.StartsApplyingAfterEffectiveDate
    ? " Si hay una recaudación en esta misma fecha, los contadores empezarán a aplicar desde la próxima recaudación posterior."
    : "";
  return `Fecha minima permitida: ${formatDateOnlyForDisplay(bounds.MinEffectiveDate)}. ${getCounterConfigDateBoundsMessage(bounds)}${nextCollectionText}`;
};

const LocalDisplayNumberChip = ({ value, onClick, disabled = false }) => {
  const hasValue = value !== null && value !== undefined && value !== "";
  const label = hasValue ? `Nro. ${value}` : "Sin Nº";

  if (disabled) {
    return <MachineMeta>{label}</MachineMeta>;
  }

  return (
    <Tooltip title="Editar Nº en local" arrow>
      <Box
        component="button"
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onClick?.(event);
        }}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          maxWidth: "100%",
          minHeight: 32,
          px: 0.5,
          py: 0.25,
          border: 0,
          borderRadius: 0,
          backgroundColor: "transparent",
          color: "primary.main",
          font: "inherit",
          fontSize: "0.90625rem",
          fontWeight: 700,
          lineHeight: 1.35,
          whiteSpace: "nowrap",
          cursor: "pointer",
          "&:hover": {
            textDecoration: "underline",
          },
          "&:focus-visible": {
            outline: "2px solid currentColor",
            outlineOffset: -2,
          },
        }}
      >
        <span>{label}</span>
      </Box>
    </Tooltip>
  );
};

function LocalMachine() {
 const location = useLocation();
const navigate = useNavigate();
const { id } = useParams();

const localFromState = location.state?.local || null;
const justCreated = !!location.state?.justCreated;
const returnTo = location.state?.returnTo || null;

const localId = Number(id);
const currentRole = getCurrentRole();
const canViewLocalCollections = currentRole === "admin" || currentRole === "user";
const incomeButtonLabel = "Ver ingresos";

const [local, setLocal] = useState(localFromState);
const isClosedLocal = local?.IsActive === false;
const isComercioLocal = normalizeLocalType(local?.Type) === "comercio";

  const [localMachineDetails, setLocalMachineDetails] = useState([]);
  const [counterConfigsByLocalMachineId, setCounterConfigsByLocalMachineId] = useState({});
  const [counterInitialValuesStatusByLocalMachineId, setCounterInitialValuesStatusByLocalMachineId] = useState({});
  const [profitPercentages, setProfitPercentages] = useState([]);
  const [pendingInitialSlotMachines, setPendingInitialSlotMachines] = useState([]);
  const [initialValuesManageableMachines, setInitialValuesManageableMachines] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [localMachineToDelete, setLocalMachineToDelete] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [localMachineToEdit, setLocalMachineToEdit] = useState(null);
  const [isEditDisplayNumberModalOpen, setIsEditDisplayNumberModalOpen] = useState(false);
  const [localMachineToEditDisplayNumber, setLocalMachineToEditDisplayNumber] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
  const [isEditFormulasModalOpen, setIsEditFormulasModalOpen] = useState(false);
  const [isEditProfitModalOpen, setIsEditProfitModalOpen] = useState(false);
  const [isInitialValuesModalOpen, setIsInitialValuesModalOpen] = useState(false);
  const [localMachineForInitialValues, setLocalMachineForInitialValues] = useState(null);
  const [isInitialValuesSaving, setIsInitialValuesSaving] = useState(false);
  const [isCounterInitialValuesModalOpen, setIsCounterInitialValuesModalOpen] = useState(false);
  const [counterInitialValuesMachine, setCounterInitialValuesMachine] = useState(null);
  const [isCounterInitialValuesSaving, setIsCounterInitialValuesSaving] = useState(false);
  const [isBulkMoveModalOpen, setIsBulkMoveModalOpen] = useState(false);
  const [isCounterConfigModalOpen, setIsCounterConfigModalOpen] = useState(false);
  const [counterConfigMachine, setCounterConfigMachine] = useState(null);
  const [counterFormulas, setCounterFormulas] = useState(null);
  const [counterConfigForm, setCounterConfigForm] = useState({
    useDifferentFormulas: false,
    sharedFormulaMode: "custom",
    sharedFormulaId: "",
    sharedFormulaType: "",
    sharedFormulaValue: "",
    inFormulaMode: "custom",
    inFormulaId: "",
    inFormulaType: "",
    inFormulaValue: "",
    outFormulaMode: "custom",
    outFormulaId: "",
    outFormulaType: "",
    outFormulaValue: "",
    CounterInFormulaType: "",
    CounterInFormulaValue: "",
    CounterOutFormulaType: "",
    CounterOutFormulaValue: "",
    ToleranceAmount: "10",
    EffectiveDate: getTodayDateOnlyValue(),
  });
  const [counterConfigPickerDate, setCounterConfigPickerDate] = useState(() => new Date());
  const [counterConfigDateBounds, setCounterConfigDateBounds] = useState(null);
  const [isCounterConfigDateBoundsLoading, setIsCounterConfigDateBoundsLoading] = useState(false);
  const [counterConfigModalError, setCounterConfigModalError] = useState("");
  const [showCounterConfigErrors, setShowCounterConfigErrors] = useState(false);
  const [isCounterConfigSaving, setIsCounterConfigSaving] = useState(false);
  const [isRemoveCounterConfigConfirmOpen, setIsRemoveCounterConfigConfirmOpen] = useState(false);
  const [removeCounterConfigError, setRemoveCounterConfigError] = useState("");
  const [isRemovingCounterConfig, setIsRemovingCounterConfig] = useState(false);

  useEffect(() => {
  if (!localFromState) return;

  setLocal((prev) => {
    if (prev?.CreatedAt && !localFromState?.CreatedAt) return prev;
    return localFromState;
  });
}, [localFromState]);

const loadLocal = useCallback(async () => {
  // Necesitamos CreatedAt para limitar correctamente la fecha al agregar máquinas.
  const hasLoadedLocal = (l) =>
    Boolean(l?.Id) && Boolean(l?.Name) && Boolean(l?.CreatedAt);

  if (hasLoadedLocal(local)) return;

  if (!Number.isFinite(localId)) return;

  try {
    // Ideal: endpoint que te devuelva el local con su Person (dueño)
    // Ajustá el nombre según tu service real:
    const fetched = await LocalService.getLocalWithOwnerById(localId);
    setLocal(fetched || null);
  } catch (e) {
    console.error("Error cargando local:", e);
    setLocal(null);
  }
}, [localFromState, local, localId]);

useEffect(() => {
  loadLocal();
}, [loadLocal]);

  const filterActiveProfit = (list) =>
    (list || []).filter((pp) => pp.IsActive !== false);
  
  // agrupar por etiqueta visible de tipo
 const groupByType = (items) =>
   (items || []).reduce((groups, item) => {
     const label = getTypeLabel(item) || "No asignado";
     (groups[label] ||= []).push(item);
     return groups;
   }, {});

     useEffect(() => {
    if (justCreated) {
      setSnack({
        open: true,
        severity: "success",
        message: "Local creado con éxito.",
      });
    }
  }, [justCreated]);
  
  const load = useCallback(async () => {
  if (!Number.isFinite(localId)) return;

  setIsLoading(true);
  try {
    const [activeList, profitData, pendingInitials, manageableInitials, counterInitialStatuses] = await Promise.all([
      LocalMachineService.getActiveLocalMachineByLocal(localId),
      LocalMachineProfitPercentageService.getLocalMachineProfitPercentagesByLocal(localId),
      VisitMachineService.getPendingInitialSlotMachinesByLocal(localId),
      VisitMachineService.getInitialValuesManageableSlotMachinesByLocal(localId),
      LocalMachineCounterConfigService.getCounterInitialValuesStatusByLocal(localId),
    ]);

    setLocalMachineDetails(activeList || []);
    setProfitPercentages(filterActiveProfit(profitData || []));
    setPendingInitialSlotMachines(pendingInitials || []);
    setInitialValuesManageableMachines(manageableInitials || []);
    setCounterInitialValuesStatusByLocalMachineId(
      (counterInitialStatuses || []).reduce((acc, row) => {
        acc[Number(row.LocalMachineId)] = row;
        return acc;
      }, {})
    );
  } catch (error) {
    if (process.env.NODE_ENV !== "production") console.error(error);
    setPendingInitialSlotMachines([]);
    setInitialValuesManageableMachines([]);
    setCounterInitialValuesStatusByLocalMachineId({});
  } finally {
    setIsLoading(false);
  }
}, [localId]); // 👈 clave: depende de id, no del objeto local

  const hasPendingInitialSlotMachines = pendingInitialSlotMachines.length > 0;
  const counterInitialValuesStatuses = useMemo(
    () => Object.values(counterInitialValuesStatusByLocalMachineId || {}),
    [counterInitialValuesStatusByLocalMachineId]
  );
  const hasPendingCounterInitialValues = counterInitialValuesStatuses.some(
    (row) => String(row?.Mode || "").toLowerCase() === "create"
  );
  const recaudacionesReturnTo = useMemo(
    () =>
      local?.Id
        ? {
            pathname: `/local/${local.Id}/recaudaciones/tragamonedas`,
            label: "Volver a recaudaciones",
            state: { local },
          }
        : null,
    [local]
  );
  const initialValuesManageableMachineIds = useMemo(
    () =>
      new Set(
        (initialValuesManageableMachines || [])
          .map((machine) => Number(machine?.LocalMachineId))
          .filter((machineId) => Number.isInteger(machineId) && machineId > 0)
      ),
    [initialValuesManageableMachines]
  );
  const initialValuesManageableModesById = useMemo(
    () =>
      new Map(
        (initialValuesManageableMachines || [])
          .map((machine) => [
            Number(machine?.LocalMachineId),
            String(machine?.Mode || "").trim().toLowerCase(),
          ])
          .filter(([machineId]) => Number.isInteger(machineId) && machineId > 0)
      ),
    [initialValuesManageableMachines]
  );

  const getInitialValuesButtonSx = useCallback((machine) => {
    const localMachineId = Number(machine?.Id ?? machine?.LocalMachineId);
    const mode = initialValuesManageableModesById.get(localMachineId);
    const isCreate = mode === "create";
    const editYellow = "#d4b200";
    const editYellowDark = "#a88900";
    const editYellowBg = "rgba(212, 178, 0, 0.10)";

    return {
      border: "1px solid",
      borderColor: isCreate ? "error.main" : editYellow,
      color: isCreate ? "error.main" : editYellow,
      borderRadius: 1.5,
      width: 34,
      height: 34,
      fontWeight: 700,
      fontSize: "1rem",
      "&:hover": {
        borderColor: isCreate ? "error.dark" : editYellowDark,
        backgroundColor: isCreate ? "error.lighter" : editYellowBg,
      },
    };
  }, [initialValuesManageableModesById]);

  const getCounterInitialValuesButtonSx = useCallback((machine) => {
    const localMachineId = Number(machine?.Id ?? machine?.LocalMachineId);
    const mode = String(counterInitialValuesStatusByLocalMachineId[localMachineId]?.Mode || "").toLowerCase();
    const isCreate = mode === "create";
    const editYellow = "#d4b200";
    const editYellowDark = "#a88900";
    const editYellowBg = "rgba(212, 178, 0, 0.10)";

    return {
      border: "1px solid",
      borderColor: isCreate ? "error.main" : editYellow,
      color: isCreate ? "error.main" : editYellow,
      borderRadius: 1.5,
      width: 34,
      height: 34,
      fontWeight: 700,
      fontSize: "1rem",
      "&:hover": {
        borderColor: isCreate ? "error.dark" : editYellowDark,
        backgroundColor: isCreate ? "error.lighter" : editYellowBg,
      },
    };
  }, [counterInitialValuesStatusByLocalMachineId]);

useEffect(() => {
  load();
}, [load]);

  // particionar normales / alquiladas por tipo
  const orderedLocalMachines = useMemo(
    () => sortLocalMachinesFixed(localMachineDetails, { preferLocalDisplayNumber: isComercioLocal }),
    [localMachineDetails, isComercioLocal]
  );

  const { notRentedByType, rentedByType, hasAnyRented } = useMemo(() => {
    const byType = groupByType(orderedLocalMachines);
    const NR = {};
    const R = {};
    let total = 0;

    Object.entries(byType).forEach(([type, arr]) => {
    const nr = arr.filter(m => !hasRent(m));
    const r  = arr.filter(m =>  hasRent(m));
      if (nr.length) NR[type] = nr;
      if (r.length)  { R[type] = r; total += r.length; }
    });

    return {
      notRentedByType: NR,
      rentedByType: R,
      hasAnyRented: total > 0,
    };
  }, [orderedLocalMachines]);

  const notRentedEntries = useMemo(
    () => sortTypeEntries(notRentedByType),
    [notRentedByType]
  );

  const rentedEntries = useMemo(
    () => sortTypeEntries(rentedByType),
    [rentedByType]
  );

  useEffect(() => {
    let cancelled = false;

    const loadCounterConfigs = async () => {
      if (!isComercioLocal) {
        setCounterConfigsByLocalMachineId({});
        return;
      }

      const candidates = (orderedLocalMachines || []).filter((machine) => {
        const localMachineId = Number(machine?.Id ?? machine?.LocalMachineId);
        return (
          Number.isInteger(localMachineId) &&
          localMachineId > 0 &&
          isTraga(getType(machine)) &&
          !hasRent(machine)
        );
      });

      if (!candidates.length) {
        setCounterConfigsByLocalMachineId({});
        return;
      }

      const entries = await Promise.all(
        candidates.map(async (machine) => {
          const localMachineId = Number(machine?.Id ?? machine?.LocalMachineId);
          try {
            const config =
              await LocalMachineCounterConfigService.getActiveLocalMachineCounterConfig(
                localMachineId
              );
            return [localMachineId, config || null];
          } catch (error) {
            console.error("Error cargando configuración activa de contadores:", error);
            return [localMachineId, null];
          }
        })
      );

      if (cancelled) return;

      const nextConfigs = entries.reduce((acc, [localMachineId, config]) => {
        if (config) acc[localMachineId] = config;
        return acc;
      }, {});
      setCounterConfigsByLocalMachineId(nextConfigs);
    };

    loadCounterConfigs();

    return () => {
      cancelled = true;
    };
  }, [orderedLocalMachines, isComercioLocal]);

  const refreshActiveCounterConfig = useCallback(async (localMachineId) => {
    const parsedLocalMachineId = Number(localMachineId);
    if (!Number.isInteger(parsedLocalMachineId) || parsedLocalMachineId <= 0) return null;

    try {
      const config =
        await LocalMachineCounterConfigService.getActiveLocalMachineCounterConfig(
          parsedLocalMachineId
        );
      setCounterConfigsByLocalMachineId((prev) => {
        const next = { ...prev };
        if (config) next[parsedLocalMachineId] = config;
        else delete next[parsedLocalMachineId];
        return next;
      });
      return config || null;
    } catch (error) {
      console.error("Error actualizando configuración activa de contadores:", error);
      return null;
    }
  }, []);

  const refreshCounterInitialValuesStatus = useCallback(async () => {
    if (!Number.isFinite(localId)) return [];

    const statuses =
      await LocalMachineCounterConfigService.getCounterInitialValuesStatusByLocal(localId);
    setCounterInitialValuesStatusByLocalMachineId(
      (statuses || []).reduce((acc, row) => {
        acc[Number(row.LocalMachineId)] = row;
        return acc;
      }, {})
    );
    return statuses || [];
  }, [localId]);

  const loadCounterFormulas = useCallback(async (force = false) => {
    if (!force && Array.isArray(counterFormulas)) return counterFormulas;

    const formulas = await CounterFormulaService.getCounterFormulas();
    setCounterFormulas(formulas);
    return formulas;
  }, [counterFormulas]);

  const getCounterFormulaOptions = useCallback(
    () => [...(counterFormulas || []), NO_COUNTER_FORMULA_OPTION, CUSTOM_COUNTER_FORMULA_OPTION],
    [counterFormulas]
  );

  const getCounterFormulaKeys = useCallback((scope) => {
    if (scope === "in") {
      return { mode: "inFormulaMode", id: "inFormulaId", type: "inFormulaType", value: "inFormulaValue" };
    }
    if (scope === "out") {
      return { mode: "outFormulaMode", id: "outFormulaId", type: "outFormulaType", value: "outFormulaValue" };
    }
    return {
      mode: "sharedFormulaMode",
      id: "sharedFormulaId",
      type: "sharedFormulaType",
      value: "sharedFormulaValue",
    };
  }, []);

  const getCounterFormulaValuesFromConfig = useCallback((config = {}, direction = "in") => {
    const prefix = direction === "out" ? "CounterOutFormula" : "CounterInFormula";
    return {
      Id: config?.[`${prefix}Id`] ?? config?.[prefix]?.Id ?? null,
      Type: config?.[`${prefix}Type`] ?? config?.[prefix]?.Type ?? "",
      Value: config?.[`${prefix}Value`] ?? config?.[prefix]?.Value ?? "",
    };
  }, []);

  const formulasAreSame = useCallback(
    (left, right) =>
      String(left?.Type || "") === String(right?.Type || "") &&
      Number(left?.Value) === Number(right?.Value),
    []
  );

  const getCounterFormulaSelection = useCallback((formula, formulas = counterFormulas || []) => {
    if (formula?.Id == null && !formula?.Type && formula?.Value === "") {
      return { mode: "none", id: "", type: "", value: "" };
    }
    const type = formula?.Type || "";
    const value =
      formula?.Value === null || formula?.Value === undefined ? "" : String(formula.Value);
    const existing = (formulas || []).find(
      (item) => String(item?.Type || "") === String(type) && Number(item?.Value) === Number(value)
    );

    return {
      mode: existing ? "existing" : "custom",
      id: existing?.Id || "",
      type,
      value,
    };
  }, [counterFormulas]);

  const getFallbackCounterFormulaSelection = useCallback((formulas = counterFormulas || []) => {
    const firstFormula = (formulas || [])[0] || null;
    return firstFormula
      ? {
          mode: "existing",
          id: firstFormula.Id,
          type: firstFormula.Type || "",
          value:
            firstFormula.Value === null || firstFormula.Value === undefined
              ? ""
              : String(firstFormula.Value),
        }
      : { mode: "custom", id: "", type: "", value: "" };
  }, [counterFormulas]);

  const buildCounterConfigForm = useCallback((config = null, formulas = counterFormulas || []) => {
    const inFormula = getCounterFormulaValuesFromConfig(config, "in");
    const outFormula = getCounterFormulaValuesFromConfig(config, "out");
    const hasConfig = config != null;
    const hasInFormula = inFormula.Id != null;
    const hasOutFormula = outFormula.Id != null;
    const bothWithoutFormula = hasConfig && !hasInFormula && !hasOutFormula;
    const sameFormula =
      bothWithoutFormula ||
      (hasInFormula && hasOutFormula && formulasAreSame(inFormula, outFormula));
    const fallbackSelection = getFallbackCounterFormulaSelection(formulas);
    const noneSelection = { mode: "none", id: "", type: "", value: "" };
    const sharedSelection = bothWithoutFormula
      ? noneSelection
      : sameFormula
      ? getCounterFormulaSelection(inFormula, formulas)
      : fallbackSelection;
    const inSelection = hasInFormula
      ? getCounterFormulaSelection(inFormula, formulas)
      : hasConfig ? noneSelection : fallbackSelection;
    const outSelection = hasOutFormula
      ? getCounterFormulaSelection(outFormula, formulas)
      : hasConfig ? noneSelection : fallbackSelection;

    return {
      useDifferentFormulas: Boolean(hasConfig && !sameFormula),
      sharedFormulaMode: sharedSelection.mode,
      sharedFormulaId: sharedSelection.id,
      sharedFormulaType: sharedSelection.type,
      sharedFormulaValue: sharedSelection.value,
      inFormulaMode: inSelection.mode,
      inFormulaId: inSelection.id,
      inFormulaType: inSelection.type,
      inFormulaValue: inSelection.value,
      outFormulaMode: outSelection.mode,
      outFormulaId: outSelection.id,
      outFormulaType: outSelection.type,
      outFormulaValue: outSelection.value,
      CounterInFormulaType: inSelection.type,
      CounterInFormulaValue: inSelection.value,
      CounterOutFormulaType: outSelection.type,
      CounterOutFormulaValue: outSelection.value,
      ToleranceAmount:
        config?.ToleranceAmount === null || config?.ToleranceAmount === undefined
          ? "10"
          : String(config.ToleranceAmount),
      EffectiveDate: getTodayDateOnlyValue(),
    };
  }, [
    counterFormulas,
    formulasAreSame,
    getCounterFormulaSelection,
    getCounterFormulaValuesFromConfig,
    getFallbackCounterFormulaSelection,
  ]);

  const handleOpenCounterConfigModal = useCallback(async (machine) => {
    const localMachineId = Number(machine?.Id ?? machine?.LocalMachineId);
    if (!Number.isInteger(localMachineId) || localMachineId <= 0) return;

    const activeConfig = counterConfigsByLocalMachineId[localMachineId] || null;
    const loadedCounterFormulas = await loadCounterFormulas();
    setCounterConfigMachine(machine);
    setCounterConfigForm(buildCounterConfigForm(activeConfig, loadedCounterFormulas));
    setCounterConfigPickerDate(parseDateOnlyToLocalDate(getTodayDateOnlyValue()));
    setCounterConfigDateBounds(null);
    setCounterConfigModalError("");
    setShowCounterConfigErrors(false);
    setIsCounterConfigModalOpen(true);

    setIsCounterConfigDateBoundsLoading(true);
    try {
      const bounds =
        await LocalMachineCounterConfigService.getLocalMachineCounterConfigDateBounds(
          localMachineId
        );
      setCounterConfigDateBounds(bounds);
      if (bounds) {
        setCounterConfigForm((prev) => ({
          ...prev,
          EffectiveDate: clampDateOnlyValue(
            prev.EffectiveDate || getTodayDateOnlyValue(),
            bounds.MinEffectiveDate,
            bounds.MaxEffectiveDate || getTodayDateOnlyValue()
          ),
        }));
        setCounterConfigPickerDate((prev) =>
          parseDateOnlyToLocalDate(
            clampDateOnlyValue(
              toLocalDateOnlyString(prev) || getTodayDateOnlyValue(),
              bounds.MinEffectiveDate,
              bounds.MaxEffectiveDate || getTodayDateOnlyValue()
            )
          )
        );
      }
    } catch (error) {
      console.error("Error cargando limites de fecha de contadores:", error);
      setCounterConfigDateBounds(null);
    } finally {
      setIsCounterConfigDateBoundsLoading(false);
    }
  }, [buildCounterConfigForm, counterConfigsByLocalMachineId, loadCounterFormulas]);

  const handleCloseCounterConfigModal = useCallback(() => {
    if (isCounterConfigSaving || isRemovingCounterConfig) return;
    setIsCounterConfigModalOpen(false);
    setCounterConfigMachine(null);
    setCounterConfigDateBounds(null);
    setIsCounterConfigDateBoundsLoading(false);
    setCounterConfigModalError("");
    setShowCounterConfigErrors(false);
    setIsRemoveCounterConfigConfirmOpen(false);
    setRemoveCounterConfigError("");
  }, [isCounterConfigSaving, isRemovingCounterConfig]);

  const handleCounterConfigFormChange = useCallback((name, value) => {
    setCounterConfigForm((prev) => {
      const next = { ...prev, [name]: value };

      if (!prev.useDifferentFormulas) {
        if (name === "sharedFormulaType") {
          next.CounterInFormulaType = value;
          next.CounterOutFormulaType = value;
        }
        if (name === "sharedFormulaValue") {
          next.CounterInFormulaValue = value;
          next.CounterOutFormulaValue = value;
        }
      }

      if (name === "inFormulaType") next.CounterInFormulaType = value;
      if (name === "inFormulaValue") next.CounterInFormulaValue = value;
      if (name === "outFormulaType") next.CounterOutFormulaType = value;
      if (name === "outFormulaValue") next.CounterOutFormulaValue = value;

      return next;
    });
    setCounterConfigModalError("");
  }, []);

  const getCounterFormulaOption = useCallback((scope) => {
    const keys = getCounterFormulaKeys(scope);

    if (counterConfigForm[keys.mode] === "custom") return CUSTOM_COUNTER_FORMULA_OPTION;
    if (counterConfigForm[keys.mode] === "none") return NO_COUNTER_FORMULA_OPTION;

    return (
      (counterFormulas || []).find(
        (formula) => Number(formula.Id) === Number(counterConfigForm[keys.id])
      ) || null
    );
  }, [counterConfigForm, counterFormulas, getCounterFormulaKeys]);

  const handleCounterFormulaOptionChange = useCallback((scope, option) => {
    const keys = getCounterFormulaKeys(scope);
    const isNone = option?.isNone === true;
    const isCustom = !option || option.isCustom;

    setCounterConfigForm((prev) => {
      const nextType = isNone ? "" : isCustom ? prev[keys.type] : option.Type;
      const nextValue = isNone ? "" : isCustom ? prev[keys.value] : String(option.Value);
      const next = {
        ...prev,
        [keys.mode]: isNone ? "none" : isCustom ? "custom" : "existing",
        [keys.id]: isNone || isCustom ? "" : option.Id,
        [keys.type]: nextType,
        [keys.value]: nextValue,
      };

      if (scope === "shared") {
        next.CounterInFormulaType = nextType;
        next.CounterInFormulaValue = nextValue;
        next.CounterOutFormulaType = nextType;
        next.CounterOutFormulaValue = nextValue;
      }
      if (scope === "in") {
        next.CounterInFormulaType = nextType;
        next.CounterInFormulaValue = nextValue;
      }
      if (scope === "out") {
        next.CounterOutFormulaType = nextType;
        next.CounterOutFormulaValue = nextValue;
      }

      return next;
    });
    setCounterConfigModalError("");
  }, [getCounterFormulaKeys]);

  const buildCounterFormulaFromForm = useCallback((scope, label, form = counterConfigForm) => {
    const keys = getCounterFormulaKeys(scope);
    const mode = form[keys.mode];
    if (mode === "none") return { formula: null };
    const selectedFormula =
      mode === "existing"
        ? (counterFormulas || []).find((formula) => Number(formula.Id) === Number(form[keys.id]))
        : null;
    const type = mode === "existing" ? selectedFormula?.Type : form[keys.type];
    const rawValue = mode === "existing" ? selectedFormula?.Value : form[keys.value];

    if (!type) return { error: `Selecciona la formula de contador ${label}.` };
    if (!["*", "/"].includes(type)) return { error: `Selecciona un tipo válido para contador ${label}.` };
    if (rawValue === "" || rawValue === null || rawValue === undefined) {
      return { error: `Ingresa el valor de la formula de contador ${label}.` };
    }

    const value = Number(rawValue);
    if (!Number.isInteger(value) || value <= 0) {
      return { error: `El valor de la formula de contador ${label} debe ser un entero mayor a 0.` };
    }

    return { formula: { Type: type, Value: value } };
  }, [counterConfigForm, counterFormulas, getCounterFormulaKeys]);

  const handleCounterUseDifferentFormulasChange = useCallback((checked) => {
    setCounterConfigForm((prev) => {
      if (checked) {
        const sharedResult = buildCounterFormulaFromForm("shared", "", prev);
        const sharedIsNone = prev.sharedFormulaMode === "none";
        const hasValidSharedFormula = Boolean(sharedResult.formula);
        const useExistingShared = prev.sharedFormulaMode === "existing" && hasValidSharedFormula;

        return {
          ...prev,
          useDifferentFormulas: true,
          inFormulaMode: sharedIsNone ? "none" : hasValidSharedFormula ? prev.sharedFormulaMode : "custom",
          inFormulaId: useExistingShared ? prev.sharedFormulaId : "",
          inFormulaType: hasValidSharedFormula ? sharedResult.formula.Type : "",
          inFormulaValue: hasValidSharedFormula ? String(sharedResult.formula.Value) : "",
          outFormulaMode: sharedIsNone ? "none" : hasValidSharedFormula ? prev.sharedFormulaMode : "custom",
          outFormulaId: useExistingShared ? prev.sharedFormulaId : "",
          outFormulaType: hasValidSharedFormula ? sharedResult.formula.Type : "",
          outFormulaValue: hasValidSharedFormula ? String(sharedResult.formula.Value) : "",
          CounterInFormulaType: hasValidSharedFormula ? sharedResult.formula.Type : "",
          CounterInFormulaValue: hasValidSharedFormula ? String(sharedResult.formula.Value) : "",
          CounterOutFormulaType: hasValidSharedFormula ? sharedResult.formula.Type : "",
          CounterOutFormulaValue: hasValidSharedFormula ? String(sharedResult.formula.Value) : "",
        };
      }

      const inResult = buildCounterFormulaFromForm("in", "de entrada", prev);
      const fallbackSelection = getFallbackCounterFormulaSelection();
      const sharedSelection = prev.inFormulaMode === "none"
        ? { mode: "none", id: "", type: "", value: "" }
        : inResult.formula
        ? getCounterFormulaSelection(inResult.formula)
        : fallbackSelection;

      return {
        ...prev,
        useDifferentFormulas: false,
        sharedFormulaMode: sharedSelection.mode,
        sharedFormulaId: sharedSelection.id,
        sharedFormulaType: sharedSelection.type,
        sharedFormulaValue: sharedSelection.value,
        CounterInFormulaType: sharedSelection.type,
        CounterInFormulaValue: sharedSelection.value,
        CounterOutFormulaType: sharedSelection.type,
        CounterOutFormulaValue: sharedSelection.value,
      };
    });
    setCounterConfigModalError("");
  }, [buildCounterFormulaFromForm, getCounterFormulaSelection, getFallbackCounterFormulaSelection]);

  const renderCounterFormulaFields = useCallback((scope, label) => {
    const keys = getCounterFormulaKeys(scope);
    const isCustom = counterConfigForm[keys.mode] === "custom";
    const optionError = showCounterConfigErrors && counterConfigForm[keys.mode] === "existing" && !getCounterFormulaOption(scope);
    const typeError = showCounterConfigErrors && isCustom && !["*", "/"].includes(counterConfigForm[keys.type]);
    const valueError = showCounterConfigErrors && isCustom && (!Number.isInteger(Number(counterConfigForm[keys.value])) || Number(counterConfigForm[keys.value]) <= 0);

    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Autocomplete
            options={getCounterFormulaOptions()}
            value={getCounterFormulaOption(scope)}
            onChange={(event, option) => handleCounterFormulaOptionChange(scope, option)}
            getOptionLabel={(option) =>
              option?.isNone
                ? "Sin fórmula"
                : option?.isCustom
                  ? "Ingresar otra formula"
                  : `${option?.Type || ""} ${option?.Value || ""}`.trim()
            }
            isOptionEqualToValue={(option, value) =>
              option?.isNone && value?.isNone
                ? true
                : option?.isCustom && value?.isCustom
                ? true
                : Number(option?.Id) === Number(value?.Id)
            }
            renderInput={(params) => <TextField {...params} label={label} required fullWidth error={optionError} helperText={optionError ? "Seleccioná una fórmula." : ""} />}
          />
        </Grid>

        {isCustom && (
          <>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Tipo formula"
                required
                value={counterConfigForm[keys.type]}
                onChange={(event) => handleCounterConfigFormChange(keys.type, event.target.value)}
                error={typeError}
                helperText={typeError ? "Seleccioná un tipo de fórmula." : ""}
              >
                <MenuItem value="*">*</MenuItem>
                <MenuItem value="/">/</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <AppNumberInput
                fullWidth
                label="Valor formula"
                required
                value={counterConfigForm[keys.value]}
                onValueChange={(value) => handleCounterConfigFormChange(keys.value, value)}
                decimalScale={0}
                allowNegative={false}
                min={1}
                error={valueError}
                helperText={valueError ? "Ingresá un valor mayor que cero." : ""}
              />
            </Grid>
          </>
        )}
      </Grid>
    );
  }, [
    counterConfigForm,
    getCounterFormulaKeys,
    getCounterFormulaOption,
    getCounterFormulaOptions,
    handleCounterConfigFormChange,
    handleCounterFormulaOptionChange,
    showCounterConfigErrors,
  ]);

  const validateCounterConfigForm = useCallback(() => {
    const localMachineId = Number(counterConfigMachine?.Id ?? counterConfigMachine?.LocalMachineId);
    if (!Number.isInteger(localMachineId) || localMachineId <= 0) {
      return "LocalMachineId inválido.";
    }
    const formulaResults = counterConfigForm.useDifferentFormulas
      ? [
          buildCounterFormulaFromForm("in", "de entrada"),
          buildCounterFormulaFromForm("out", "de salida"),
        ]
      : [buildCounterFormulaFromForm("shared", "")];
    const formulaError = formulaResults.find((result) => result.error)?.error;
    if (formulaError) return formulaError.replace(" contador .", " contador.");

    if (
      counterConfigForm.ToleranceAmount === "" ||
      counterConfigForm.ToleranceAmount === null ||
      counterConfigForm.ToleranceAmount === undefined
    ) {
      return "La tolerancia debe ser un número mayor o igual a 0.";
    }
    const toleranceAmount = Number(counterConfigForm.ToleranceAmount);
    if (!Number.isFinite(toleranceAmount) || toleranceAmount < 0) {
      return "La tolerancia debe ser un número mayor o igual a 0.";
    }

    if (!counterConfigForm.EffectiveDate) {
      return "Selecciona una fecha efectiva válida.";
    }
    if (!isValidDateOnlyValue(counterConfigForm.EffectiveDate)) {
      return "Selecciona una fecha efectiva válida.";
    }
    if (isFutureDateOnlyValue(counterConfigForm.EffectiveDate)) {
      return "La fecha efectiva no puede ser futura.";
    }
    if (
      counterConfigDateBounds?.MinEffectiveDate &&
      counterConfigForm.EffectiveDate < counterConfigDateBounds.MinEffectiveDate
    ) {
      return getCounterConfigDateBoundsMessage(counterConfigDateBounds);
    }
    if (
      counterConfigDateBounds?.MaxEffectiveDate &&
      counterConfigForm.EffectiveDate > counterConfigDateBounds.MaxEffectiveDate
    ) {
      return "La fecha efectiva no puede ser posterior a la fecha maxima permitida.";
    }

    return "";
  }, [
    buildCounterFormulaFromForm,
    counterConfigDateBounds,
    counterConfigForm,
    counterConfigMachine,
  ]);

  const handleSaveCounterConfig = useCallback(async () => {
    setShowCounterConfigErrors(true);
    const validationError = validateCounterConfigForm();
    if (validationError) {
      const isFieldError = /fórmula|formula|tolerancia|fecha/i.test(validationError);
      setCounterConfigModalError(isFieldError ? "" : validationError);
      return;
    }

    const localMachineId = Number(counterConfigMachine?.Id ?? counterConfigMachine?.LocalMachineId);
    const sharedFormula = !counterConfigForm.useDifferentFormulas
      ? buildCounterFormulaFromForm("shared", "").formula
      : null;
    const counterInFormula = counterConfigForm.useDifferentFormulas
      ? buildCounterFormulaFromForm("in", "de entrada").formula
      : sharedFormula;
    const counterOutFormula = counterConfigForm.useDifferentFormulas
      ? buildCounterFormulaFromForm("out", "de salida").formula
      : sharedFormula;
    const payload = {
      LocalMachineId: localMachineId,
      CounterInFormula: counterInFormula,
      CounterOutFormula: counterOutFormula,
      ToleranceAmount: Number(counterConfigForm.ToleranceAmount),
      EffectiveDate: counterConfigForm.EffectiveDate,
    };

    setIsCounterConfigSaving(true);
    setCounterConfigModalError("");

    try {
      await LocalMachineCounterConfigService.createLocalMachineCounterConfig(payload);
      await Promise.all([
        loadCounterFormulas(true),
        refreshActiveCounterConfig(localMachineId),
        refreshCounterInitialValuesStatus(),
      ]);
      setSnack({
        open: true,
        severity: "success",
        message: "Configuración de contadores guardada.",
      });
      setIsCounterConfigModalOpen(false);
      setCounterConfigMachine(null);
      setCounterConfigDateBounds(null);
    } catch (error) {
      console.error("Error guardando configuración de contadores:", error);
      const errorData = error?.response?.data || {};
      setCounterConfigModalError(
        errorData.error ||
          errorData.message ||
          errorData.code ||
          "No se pudo guardar la configuración de contadores."
      );
    } finally {
      setIsCounterConfigSaving(false);
    }
  }, [
    buildCounterFormulaFromForm,
    counterConfigForm,
    counterConfigMachine,
    loadCounterFormulas,
    refreshActiveCounterConfig,
    refreshCounterInitialValuesStatus,
    validateCounterConfigForm,
  ]);

  /* ====== MODALES: handlers ====== */
  const handleOpenCreateModal = () => setIsCreateModalOpen(true); 
  const handleCloseCreateModal = () => setIsCreateModalOpen(false);
  
  const handleOpenDeleteModal = (id) => {
    const selected = localMachineDetails.find((m) => m.Id === id);
    setLocalMachineToDelete(selected);
    setIsDeleteModalOpen(true);
  };
  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setLocalMachineToDelete(null);
  };
  const handleDeleteSuccess = async (profitPctDeleted = false) => {
    await refreshCollectionPrerequisites();
    setSnack({
      open: true,
      severity: "success",
      message: profitPctDeleted
        ? "Máquina eliminada. También se eliminó el porcentaje para ese tipo en este local."
        : "Máquina eliminada correctamente.",
    });
    handleCloseDeleteModal();
  };

  const handleOpenEditModal = (id) => {
  const selected = localMachineDetails.find((m) => m.Id === id);
  setLocalMachineToEdit(selected);
  setIsEditModalOpen(true);
  };
  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setLocalMachineToEdit(null);
  };

  const handleOpenEditDisplayNumberModal = (id) => {
    const selected = localMachineDetails.find((m) => m.Id === id);
    setLocalMachineToEditDisplayNumber(selected);
    setIsEditDisplayNumberModalOpen(true);
  };
  const handleCloseEditDisplayNumberModal = () => {
    setIsEditDisplayNumberModalOpen(false);
    setLocalMachineToEditDisplayNumber(null);
  };
  const refreshCollectionPrerequisites = useCallback(async () => {
    if (!Number.isFinite(localId)) return;

    const [details, profits, pendingInitials, manageableInitials, counterInitialStatuses] = await Promise.all([
      LocalMachineService.getActiveLocalMachineByLocal(localId),
      LocalMachineProfitPercentageService.getLocalMachineProfitPercentagesByLocal(localId),
      VisitMachineService.getPendingInitialSlotMachinesByLocal(localId),
      VisitMachineService.getInitialValuesManageableSlotMachinesByLocal(localId),
      LocalMachineCounterConfigService.getCounterInitialValuesStatusByLocal(localId),
    ]);

    setLocalMachineDetails(details || []);
    setProfitPercentages(filterActiveProfit(profits || []));
    setPendingInitialSlotMachines(pendingInitials || []);
    setInitialValuesManageableMachines(manageableInitials || []);
    const counterInitialStatusMap = (counterInitialStatuses || []).reduce((acc, row) => {
      acc[Number(row.LocalMachineId)] = row;
      return acc;
    }, {});
    setCounterInitialValuesStatusByLocalMachineId(counterInitialStatusMap);
    return {
      details: details || [],
      profits: profits || [],
      pendingInitials: pendingInitials || [],
      manageableInitials: manageableInitials || [],
      counterInitialStatuses: counterInitialStatuses || [],
      counterInitialStatusMap,
    };
  }, [localId]);

  const handleOpenRemoveCounterConfigConfirm = useCallback(() => {
    const effectiveDate = counterConfigForm.EffectiveDate;
    if (!isValidDateOnlyValue(effectiveDate)) {
      setCounterConfigModalError("Selecciona una fecha efectiva válida.");
      return;
    }
    if (isFutureDateOnlyValue(effectiveDate)) {
      setCounterConfigModalError("La fecha efectiva no puede ser futura.");
      return;
    }
    if (
      counterConfigDateBounds?.MinEffectiveDate &&
      effectiveDate < counterConfigDateBounds.MinEffectiveDate
    ) {
      setCounterConfigModalError(getCounterConfigDateBoundsMessage(counterConfigDateBounds));
      return;
    }
    if (
      counterConfigDateBounds?.MaxEffectiveDate &&
      effectiveDate > counterConfigDateBounds.MaxEffectiveDate
    ) {
      setCounterConfigModalError(
        "La fecha efectiva no puede ser posterior a la fecha maxima permitida."
      );
      return;
    }

    setCounterConfigModalError("");
    setRemoveCounterConfigError("");
    setIsRemoveCounterConfigConfirmOpen(true);
  }, [counterConfigDateBounds, counterConfigForm.EffectiveDate]);

  const handleCloseRemoveCounterConfigConfirm = useCallback(() => {
    if (isRemovingCounterConfig) return;
    setIsRemoveCounterConfigConfirmOpen(false);
    setRemoveCounterConfigError("");
  }, [isRemovingCounterConfig]);

  const handleRemoveCounterConfig = useCallback(async () => {
    const localMachineId = Number(
      counterConfigMachine?.Id ?? counterConfigMachine?.LocalMachineId
    );
    if (!Number.isInteger(localMachineId) || localMachineId <= 0) {
      setRemoveCounterConfigError("LocalMachineId inválido.");
      return;
    }
    if (!isValidDateOnlyValue(counterConfigForm.EffectiveDate)) {
      setRemoveCounterConfigError("Selecciona una fecha efectiva válida.");
      return;
    }
    if (isFutureDateOnlyValue(counterConfigForm.EffectiveDate)) {
      setRemoveCounterConfigError("La fecha efectiva no puede ser futura.");
      return;
    }
    if (
      counterConfigDateBounds?.MinEffectiveDate &&
      counterConfigForm.EffectiveDate < counterConfigDateBounds.MinEffectiveDate
    ) {
      setRemoveCounterConfigError(getCounterConfigDateBoundsMessage(counterConfigDateBounds));
      return;
    }
    if (
      counterConfigDateBounds?.MaxEffectiveDate &&
      counterConfigForm.EffectiveDate > counterConfigDateBounds.MaxEffectiveDate
    ) {
      setRemoveCounterConfigError(
        "La fecha efectiva no puede ser posterior a la fecha maxima permitida."
      );
      return;
    }

    setIsRemovingCounterConfig(true);
    setRemoveCounterConfigError("");

    try {
      const result =
        await LocalMachineCounterConfigService.removeActiveCounterConfigFromLocalMachine(
          localMachineId,
          { EffectiveDate: counterConfigForm.EffectiveDate }
        );
      await Promise.all([
        refreshActiveCounterConfig(localMachineId),
        refreshCollectionPrerequisites(),
      ]);

      setSnack({
        open: true,
        severity: result?.removed === false ? "info" : "success",
        message:
          result?.removed === false
            ? "La máquina ya no tenía contadores activos."
            : "Contadores quitados correctamente.",
      });
      setIsRemoveCounterConfigConfirmOpen(false);
      setIsCounterConfigModalOpen(false);
      setCounterConfigMachine(null);
      setCounterConfigDateBounds(null);
    } catch (error) {
      console.error("Error quitando contadores:", error);
      const errorData = error?.response?.data || {};
      setRemoveCounterConfigError(
        errorData.error ||
          errorData.message ||
          errorData.code ||
          "No se pudieron quitar los contadores."
      );
    } finally {
      setIsRemovingCounterConfig(false);
    }
  }, [
    counterConfigDateBounds,
    counterConfigForm.EffectiveDate,
    counterConfigMachine,
    refreshActiveCounterConfig,
    refreshCollectionPrerequisites,
  ]);

  const syncInitialValuesModalContext = useCallback((refreshResult) => {
    if (!refreshResult || !localMachineForInitialValues?.Id) return;

    const currentLocalMachineId = Number(localMachineForInitialValues.Id);
    const refreshedLocalMachine =
      (refreshResult.details || []).find((machine) => Number(machine?.Id) === currentLocalMachineId) ||
      null;
    setLocalMachineForInitialValues(refreshedLocalMachine ?? localMachineForInitialValues);
  }, [localMachineForInitialValues]);
  const handleOpenInitialValuesModal = useCallback((localMachine) => {
    if (!localMachine?.Id) return;

    setLocalMachineForInitialValues(localMachine);
    setIsInitialValuesModalOpen(true);
  }, []);
  const handleCloseInitialValuesModal = useCallback(() => {
    if (isInitialValuesSaving) return;
    setIsInitialValuesModalOpen(false);
    setLocalMachineForInitialValues(null);
    setIsInitialValuesSaving(false);
  }, [isInitialValuesSaving]);
  const handleInitialValuesSuccess = useCallback(async () => {
    const refreshResult = await refreshCollectionPrerequisites();
    setSnack({
      open: true,
      severity: "success",
      message: "Valores iniciales guardados correctamente.",
    });
    syncInitialValuesModalContext(refreshResult);
    handleCloseInitialValuesModal();
  }, [refreshCollectionPrerequisites, syncInitialValuesModalContext, handleCloseInitialValuesModal]);
  const handleInitialValuesStateRefresh = useCallback(async () => {
    const refreshResult = await refreshCollectionPrerequisites();
    syncInitialValuesModalContext(refreshResult);
  }, [refreshCollectionPrerequisites, syncInitialValuesModalContext]);

  const handleOpenCounterInitialValuesModal = useCallback((localMachine) => {
    const localMachineId = Number(localMachine?.Id ?? localMachine?.LocalMachineId);
    const status = counterInitialValuesStatusByLocalMachineId[localMachineId] || null;
    if (!status || status.Mode === "locked") return;

    setCounterInitialValuesMachine(localMachine);
    setIsCounterInitialValuesModalOpen(true);
  }, [counterInitialValuesStatusByLocalMachineId]);

  const handleCloseCounterInitialValuesModal = useCallback(() => {
    if (isCounterInitialValuesSaving) return;
    setIsCounterInitialValuesModalOpen(false);
    setCounterInitialValuesMachine(null);
    setIsCounterInitialValuesSaving(false);
  }, [isCounterInitialValuesSaving]);

  const handleCounterInitialValuesSuccess = useCallback(async () => {
    await refreshCollectionPrerequisites();
    setSnack({
      open: true,
      severity: "success",
      message: "Valores iniciales de contadores guardados correctamente.",
    });
    handleCloseCounterInitialValuesModal();
  }, [refreshCollectionPrerequisites, handleCloseCounterInitialValuesModal]);
  const handleEditSuccess = async () => {
  if (!Number.isFinite(localId)) return;
  await refreshCollectionPrerequisites();
  setSnack({ open: true, severity: "success", message: "Configuración actualizada." });
  handleCloseEditModal();
};

  const handleEditDisplayNumberSuccess = async () => {
    if (!Number.isFinite(localId)) return;
    await refreshCollectionPrerequisites();
    setSnack({ open: true, severity: "success", message: "Nº en local actualizado." });
    handleCloseEditDisplayNumberModal();
  };

  const handleOpenEditFormulasModal = () => setIsEditFormulasModalOpen(true);
const handleCloseEditFormulasModal = () => setIsEditFormulasModalOpen(false);

const handleEditFormulasSuccess = async () => {
  if (!Number.isFinite(localId)) return;
  await refreshCollectionPrerequisites();
  setSnack({
    open: true,
    severity: "success",
    message: "Fórmulas actualizadas para todas las máquinas de ese tipo.",
  });
  handleCloseEditFormulasModal();
};

  // ====== MODAL Editar porcentajes cliente/dueño ======
  const handleOpenEditProfitModal = () => setIsEditProfitModalOpen(true);
  const handleCloseEditProfitModal = () => setIsEditProfitModalOpen(false);

  const handleEditProfitSuccess = async () => {
     if (!Number.isFinite(localId)) return;
    await refreshCollectionPrerequisites();
    setSnack({
      open: true,
      severity: "success",
      message: "Porcentajes actualizados para este local.",
    });
    handleCloseEditProfitModal();
  };

    // ====== MODAL mover TODAS las máquinas a depósito(s) ======
  const handleOpenBulkMoveModal = () => setIsBulkMoveModalOpen(true);
  const handleCloseBulkMoveModal = () => setIsBulkMoveModalOpen(false);

  const handleBulkMoveSuccess = async () => {
    if (isBulkMoveModalOpen) handleCloseBulkMoveModal();
    navigate("/local", {
      state: {
        snack: {
          open: true,
          severity: "success",
          message: "Local cerrado correctamente.",
        },
      },
    });
  };

  const handleCloseLocalClick = async () => {
    handleOpenBulkMoveModal();
  };

const MachineLine = ({
  m,
  rented = false,
  onEditDisplayNumber,
}) => {
  const sn = m?.Machine?.SerialNumber;
  const name = m?.Machine?.Name || "Sin nombre";
  const title = [name, sn].filter((part) => part != null && String(part).trim()).join(" · ");
  const t = getType(m);
  const localDisplayNumber = m?.LocalDisplayNumber;

  // recalcular rented por seguridad si no vino por prop
  const isRented = rented || hasRent(m);

  let infos = [];
  if (!isRented) {
    if (isFichaType(t)) {
      const val = getFormulaValue(m);
      infos.push(`Ficha: ${val != null ? money(val) : "No asignada"}`);
    } else if (isTraga(t)) {
      const formulaName = getFormulaName(m);
      const formulaText = String(formulaName || "").trim();
      const hasFormulaLabel = formulaText && !formulaText.toLowerCase().startsWith("no asignad");
      const hasFormulaReference =
        (m?.FormulaId != null && m.FormulaId !== "") || m?.Formula?.Id != null;
      const formulaDisplay = hasFormulaLabel
        ? formulaText
        : hasFormulaReference
          ? "Sin dato"
          : "$";

      // flag de acumulativa/no acumulativa (solo si NO está alquilada)
      const raw = m?.IsAccumulative;
      const isAcc =
        raw === true || raw === false
          ? raw
          : raw == null
            ? null
            : Number(raw) === 1
              ? true
              : Number(raw) === 0
                ? false
                : null;
      const modeText =
        isAcc === false ? "Borrado" : isAcc === null ? getAccumulationModeLabel(isAcc) : "";
      infos.push([modeText, formulaDisplay].filter(Boolean).join(" "));
    }
  } else {
    const price = getRentPrice(m);
    const freq  = getRentFrequency(m);
    infos.push(`Alquiler: ${price != null ? money(price) : "—"}`);
    infos.push(`Frecuencia: ${freq || "—"}.`);
  }

  return (
    <Box sx={{ minWidth: 0, width: "100%" }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
      {isComercioLocal && !isRented && isTraga(t) && (
        <>
          <LocalDisplayNumberChip
            value={localDisplayNumber}
            onClick={() => onEditDisplayNumber?.(m.Id)}
          />
          <span style={{ opacity: 0.55, userSelect: "none" }}>·</span>
        </>
      )}
      <MachineName>{title}</MachineName>
      {infos.length > 0 && (
        <>
          <span style={{ opacity: 0.55, userSelect: "none" }}>·</span>
          <MachineMeta>{infos.join("  •  ")}</MachineMeta>
        </>
      )}
    </Box>
    </Box>
  );
};

const shouldShowInitialValuesAction = (machine, manageableIds) => {
  const localMachineId = Number(machine?.Id ?? machine?.LocalMachineId);
  if (!Number.isInteger(localMachineId) || localMachineId <= 0) return false;
  if (!isTraga(getType(machine))) return false;
  return manageableIds.has(localMachineId);
};

const shouldShowCounterInitialValuesAction = (machine) => {
  const localMachineId = Number(machine?.Id ?? machine?.LocalMachineId);
  if (!Number.isInteger(localMachineId) || localMachineId <= 0) return false;
  if (!isComercioLocal || !isTraga(getType(machine)) || hasRent(machine)) return false;
  const status = counterInitialValuesStatusByLocalMachineId[localMachineId] || null;
  const mode = String(status?.Mode || "").toLowerCase();
  return mode === "create" || mode === "edit";
};

const getCounterInitialValuesTooltip = (machine) => {
  const localMachineId = Number(machine?.Id ?? machine?.LocalMachineId);
  const mode = String(counterInitialValuesStatusByLocalMachineId[localMachineId]?.Mode || "").toLowerCase();
  return mode === "edit"
    ? "Editar valores iniciales de contadores"
    : "Ingresar valores iniciales de contadores";
};

// ====== TÍTULO dinámico del modal EDITAR ======
const getEditDialogTitle = (lm) => {
  if (!lm) return "Editar configuración";

  const typeLabel = getType(lm) || "Sin tipo";
  const machineName = isComercioLocal
    ? buildMachineDisplayName(lm, { fallback: "Sin nombre" })
    : (lm?.Machine?.Name || "Sin nombre");

  // 1) Si está alquilada (prioridad)
  if (hasRent(lm)) {
    return `Modificar alquiler de ${typeLabel} ${machineName}`;
  }

  // 2) Si es Tragamoneda (no alquilada)
  if (isTraga(typeLabel)) {
    return `Modificar fórmula de ${typeLabel} ${machineName}`;
  }

  // Fallback (por si en el futuro habilitás otros tipos)
  return `Editar configuración de ${typeLabel} ${machineName}`;
};

// ====== TÍTULO dinámico del modal ELIMINAR ======
const getDeleteDialogTitle = (lm) => {
  if (!lm) return "Retirar máquina del local";

  const typeLabel = getType(lm) || "Sin tipo";
  const machineName = isComercioLocal
    ? buildMachineDisplayName(lm, { fallback: "Sin nombre" })
    : (lm?.Machine?.Name || "Sin nombre");

  return `Retirar ${typeLabel} ${machineName}`;
};

const headerContainedBtnSx = {
  color: "#fff",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.02em",
  // mantiene el look del header sin “destacar” uno solo
  backgroundColor: "rgba(255,255,255,0.14)",
  "&:hover": { backgroundColor: "rgba(255,255,255,0.22)" },
};

const counterConfigLocalMachineId = Number(
  counterConfigMachine?.Id ?? counterConfigMachine?.LocalMachineId
);
const activeCounterConfigForModal =
  Number.isInteger(counterConfigLocalMachineId) && counterConfigLocalMachineId > 0
    ? counterConfigsByLocalMachineId[counterConfigLocalMachineId] || null
    : null;
const counterConfigModalTitle = activeCounterConfigForModal
  ? "Editar contadores"
  : "Configurar contadores";
const currentCounterInFormula = activeCounterConfigForModal
  ? formatFormulaLabel(getCounterFormulaValuesFromConfig(activeCounterConfigForModal, "in"), { fallback: "$" })
  : null;
const currentCounterOutFormula = activeCounterConfigForModal
  ? formatFormulaLabel(getCounterFormulaValuesFromConfig(activeCounterConfigForModal, "out"), { fallback: "$" })
  : null;
const counterConfigContextLines = [
  local?.Name ? `Local: ${local.Name}` : null,
  activeCounterConfigForModal
    ? currentCounterInFormula === currentCounterOutFormula
      ? `Fórmula actual de contadores: ${currentCounterInFormula}`
      : `Fórmulas actuales · Entrada: ${currentCounterInFormula} · Salida: ${currentCounterOutFormula}`
    : "Sin contadores configurados",
  activeCounterConfigForModal
    ? `Tolerancia actual: $ ${activeCounterConfigForModal.ToleranceAmount ?? "—"}`
    : null,
].filter(Boolean);
const counterConfigDateHelperText = getCounterConfigDateBoundsHelperText(
  counterConfigDateBounds,
  isCounterConfigDateBoundsLoading
);
const counterConfigDateFieldError = showCounterConfigErrors
  ? !isValidDateOnlyValue(counterConfigForm.EffectiveDate)
    ? "Seleccioná una fecha efectiva válida."
    : isFutureDateOnlyValue(counterConfigForm.EffectiveDate)
      ? "La fecha efectiva no puede ser futura."
      : counterConfigDateBounds?.MinEffectiveDate && counterConfigForm.EffectiveDate < counterConfigDateBounds.MinEffectiveDate
        ? getCounterConfigDateBoundsMessage(counterConfigDateBounds)
        : counterConfigDateBounds?.MaxEffectiveDate && counterConfigForm.EffectiveDate > counterConfigDateBounds.MaxEffectiveDate
          ? "La fecha efectiva supera la fecha máxima permitida."
          : ""
  : "";
const counterToleranceFieldError = showCounterConfigErrors && (counterConfigForm.ToleranceAmount === "" || !Number.isFinite(Number(counterConfigForm.ToleranceAmount)) || Number(counterConfigForm.ToleranceAmount) < 0);
const counterConfigMaxEffectiveDate =
  counterConfigDateBounds?.MaxEffectiveDate || getTodayDateOnlyValue();
const localNameForCreate = String(local?.Name || "").trim();
const ownerNameForCreate = String(local?.Person?.Name || "").trim();
const createMachineDialogTitle = localNameForCreate
  ? `Agregar máquina a ${localNameForCreate}${ownerNameForCreate ? ` · ${ownerNameForCreate}` : ""}`
  : "Agregar máquina";

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <TypeBlocksGlobalStyles rootId="#local-machines" />

      <GlobalStyles
        styles={{
          "#local-machines .vtb": { borderRadius: `10px !important`, overflow: "hidden" },
          "#local-machines .vtb-inner": { borderRadius: `10px !important`, overflow: "hidden" },
        }}
      />

      <MDBox pt={3} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2} mt={-3} py={3} px={2}
                variant="gradient" bgColor="info" borderRadius="lg" coloredShadow="info"
                display="flex" justifyContent="space-between"
              >
                <MDTypography variant="h6" color="white">
                  Local {local ? local.Name : "Cargando..."}.{" "}
                  Cliente: {local ? (local.Person?.Name ?? "Sin dueño") : "Cargando..."}.
                </MDTypography>

                <Box sx={{ display: "flex", gap: 1 }}>
                  {!isClosedLocal && (
                    <>
                  <Button
                    variant="contained"
                    color="inherit"
                    disableElevation
                    sx={headerContainedBtnSx}
                    onClick={handleOpenEditFormulasModal}
                  >
                    Actualizar precio ficha
                  </Button>

                  <Button
                    variant="contained"
                    color="inherit"
                    disableElevation
                    sx={headerContainedBtnSx}
                    onClick={handleOpenEditProfitModal}
                  >
                    Editar porcentajes
                  </Button>

                  <Button
                    variant="contained"
                    color="inherit"
                    disableElevation
                    sx={headerContainedBtnSx}
                    onClick={handleCloseLocalClick}
                  >
                    Cerrar local
                  </Button>

                  <Button
                    variant="contained"
                    color="inherit"
                    disableElevation
                    sx={headerContainedBtnSx}
                    onClick={handleOpenCreateModal}
                  >
                    Agregar máquina
                  </Button>
                    </>
                  )}

                  <Button
                    variant="contained"
                    color="inherit"
                    disableElevation
                    sx={headerContainedBtnSx}
                    onClick={() => {
                      if (returnTo?.pathname) {
                        navigate(
                          {
                            pathname: returnTo.pathname,
                            search: returnTo.search || "",
                            hash: returnTo.hash || "",
                          },
                          { state: returnTo.state ?? null }
                        );
                        return;
                      }
                      navigate("/local");
                    }}
                  >
                    Volver a locales
                  </Button>
                </Box>

              </MDBox>

              {/* ====== Datos del local ====== */}
              <Accordion
                key={id}
                sx={{
                  mx: 2,
                  mt: 2,
                  boxShadow: "none",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: "8px !important",
                  "&:before": { display: "none" },
                  "&.Mui-expanded": { mt: 2, mx: 2 },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    minHeight: 48,
                    px: 2,
                    "&.Mui-expanded": { minHeight: 48 },
                    "& .MuiAccordionSummary-content": { my: 1 },
                    "& .MuiAccordionSummary-content.Mui-expanded": { my: 1 },
                    "&.Mui-focusVisible": {
                      bgcolor: "action.focus",
                      outline: "2px solid",
                      outlineColor: "primary.main",
                      outlineOffset: -2,
                    },
                  }}
                >
                  <MDTypography variant="subtitle1" fontWeight="medium">
                    Datos del local
                  </MDTypography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <MDTypography variant="subtitle1"><strong>Local</strong></MDTypography>
                      <MDTypography variant="body2"><strong>Nombre:</strong> {local?.Name}</MDTypography>
                      <MDTypography variant="body2"><strong>Dirección:</strong> {local?.Address}, {local?.City}, {local?.Department}</MDTypography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <MDTypography variant="subtitle1"><strong>Dueño</strong></MDTypography>
                      <MDTypography variant="body2"><strong>Nombre:</strong> {local?.Person?.Name}</MDTypography>
                      <MDTypography variant="body2"><strong>Contacto:</strong> {displayOptionalPersonField(local?.Person?.Contact)}</MDTypography>
                      <MDTypography variant="body2"><strong>Cuenta:</strong> {displayOptionalPersonField(local?.Person?.Account)}</MDTypography>
                      <MDTypography variant="body2"><strong>Dirección:</strong> {displayOptionalPersonField(local?.Person?.Address)}, {local?.Person?.City}, {local?.Person?.Department}</MDTypography>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              <MDBox id="local-machines" mx={2} mt={1} py={3} px={2}>
                {isLoading ? (
                  <MDTypography>Cargando máquinas...</MDTypography>
                ) : localMachineDetails.length === 0 ? (
                  <MDTypography>No hay máquinas en este local.</MDTypography>
                ) : (
                  <>
                    {/* ---- NO alquiladas por tipo ---- */}
                    {notRentedEntries.map(([label, machines]) => {
                      const typeLabel = machineTypeLabel(label, machines.length, { capitalize: true });
                      const skinType  = getTypeKey(label);

                      return (
                        <TypeBlock
                          key={`nr-${label}`}
                          type={skinType}
                          header={<TypeChipsHeader type={skinType} typeLabel={typeLabel} />}
                        >
                          <List dense>
                            {machines.map((m) => (
                              <ListItem
                                key={m.Id}
                                secondaryAction={
                                  <Box sx={{ display: "flex", gap: 1 }}>
                                  {shouldShowInitialValuesAction(m, initialValuesManageableMachineIds) && (
                                    <IconButton
                                      name="initialValuesPerMachine"
                                      aria-label="ingresar valores iniciales"
                                      color={
                                        initialValuesManageableModesById.get(Number(m?.Id ?? m?.LocalMachineId)) === "create"
                                          ? "error"
                                          : "warning"
                                      }
                                      onClick={() => handleOpenInitialValuesModal(m)}
                                      sx={getInitialValuesButtonSx(m)}
                                    >
                                      !
                                    </IconButton>
                                  )}
                                  {shouldShowCounterInitialValuesAction(m) && (
                                    <Tooltip title={getCounterInitialValuesTooltip(m)} arrow>
                                      <IconButton
                                        name="counterInitialValues"
                                        aria-label={getCounterInitialValuesTooltip(m)}
                                        color={
                                          counterInitialValuesStatusByLocalMachineId[
                                            Number(m?.Id ?? m?.LocalMachineId)
                                          ]?.Mode === "create"
                                            ? "error"
                                            : "warning"
                                        }
                                        onClick={() => handleOpenCounterInitialValuesModal(m)}
                                        sx={getCounterInitialValuesButtonSx(m)}
                                      >
                                        #
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                  {!hasRent(m) &&
                                    isComercioLocal &&
                                    isTraga(getType(m)) && (
                                    <Tooltip
                                      title={
                                        counterConfigsByLocalMachineId[
                                          Number(m?.Id ?? m?.LocalMachineId)
                                        ]
                                          ? "Editar contadores"
                                          : "Configurar contadores"
                                      }
                                      arrow
                                    >
                                      <IconButton
                                        name="editCounters"
                                        aria-label="Editar contadores"
                                        color="primary"
                                        onClick={() => handleOpenCounterConfigModal(m)}
                                      >
                                        <SpeedIcon />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                  {/* Editar: visible si NO es alquilada y NO es Rockola y NO es Pool/Tejo/Peluchera*/}
                                  {(
                                    !hasRent(m) &&
                                    getType(m) !== "Rockola" &&
                                    !isFichaType(getType(m))   // <<< Pool / Tejo / Peluchera quedan excluidas
                                  ) && (
                                    <IconButton
                                      name="editLocalMachine"
                                      aria-label="edit"
                                      color="primary"
                                      onClick={() => handleOpenEditModal(m.Id)}
                                    >
                                      <EditIcon />
                                    </IconButton>
                                  )}
                                  <IconButton
                                    name="deleteLocalMachine"
                                    aria-label="delete"
                                    color="secondary"
                                    onClick={() => handleOpenDeleteModal(m.Id)}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Box>
                                }
                                sx={{ borderBottom: "1px dashed rgba(0,0,0,.08)", py: 1.1 }}
                              >
                                <ListItemText
                                  primary={
                                    <MachineLine
                                      m={m}
                                      onEditDisplayNumber={handleOpenEditDisplayNumberModal}
                                    />
                                  }
                                  primaryTypographyProps={{ component: "div" }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </TypeBlock>
                      );
                    })}

                    {/* ---- ALQUILADAS por tipo ---- */}
                    {hasAnyRented && (
                      <Box mt={1.5}>
                        {rentedEntries.map(([label, rented]) => {
                          const typeLabel = machineTypeLabel(label, rented.length, { capitalize: true });
                          const skinType  = getTypeKey(label);
                          const gender = machineTypeGender(label);
                          const rentedTxt =
                            (gender === "m" ? "Alquilado" : "Alquilada") +
                            (rented.length === 1 ? "" : "s");

                          return (
                            <TypeBlock
                              key={`r-${label}`}
                              type={skinType}
                              header={<TypeChipsHeader type={skinType} typeLabel={typeLabel} rightChip={rentedTxt} />}
                            >
                              <List dense>
                                {rented.map((m) => (
                                  <ListItem
                                    key={m.Id}
                                    secondaryAction={
                                      <Box sx={{ display: "flex", gap: 1 }}>
                                        {shouldShowInitialValuesAction(m, initialValuesManageableMachineIds) && (
                                          <IconButton
                                            name="initialValuesPerMachine"
                                            aria-label="ingresar valores iniciales"
                                            color={
                                              initialValuesManageableModesById.get(Number(m?.Id ?? m?.LocalMachineId)) === "create"
                                                ? "error"
                                                : "warning"
                                            }
                                            onClick={() => handleOpenInitialValuesModal(m)}
                                            sx={getInitialValuesButtonSx(m)}
                                          >
                                            !
                                          </IconButton>
                                        )}
                                        <IconButton
                                          name="editLocalMachine"
                                          aria-label="edit"
                                          color="primary"
                                          onClick={() => handleOpenEditModal(m.Id)}
                                        >
                                          <EditIcon />
                                        </IconButton>
                                        <IconButton
                                          name="deleteLocalMachine"
                                          aria-label="delete"
                                          color="secondary"
                                          onClick={() => handleOpenDeleteModal(m.Id)}
                                        >
                                          <DeleteIcon />
                                        </IconButton>
                                      </Box>
                                    }
                                    sx={{ borderBottom: "1px dashed rgba(0,0,0,.08)", py: 1.1 }}
                                  >
                                    <ListItemText
                                      primary={
                                        <MachineLine
                                          m={m}
                                          rented
                                          onEditDisplayNumber={handleOpenEditDisplayNumberModal}
                                        />
                                      }
                                      primaryTypographyProps={{ component: "div" }}
                                    />
                                  </ListItem>
                                ))}
                              </List>
                            </TypeBlock>
                          );
                        })}
                      </Box>
                    )}
                  </>
                )}

                {local && localMachineDetails.length > 0 && (
                  <>
                    {/* El flujo global viejo de initialValues queda como legacy.
                        El camino visible y principal es el inicio de ciclo por máquina con el botón "!". */}
                    {hasPendingInitialSlotMachines && (
                      <Box display="flex" justifyContent="center" mt={3} mb={2}>
                        <Alert severity="warning" sx={{ width: "100%", maxWidth: 900 }}>
                          Existen tragamonedas pendientes de valores iniciales. No se puede
                          ingresar recaudación sin valores iniciales.
                        </Alert>
                      </Box>
                    )}
                    {hasPendingCounterInitialValues && (
                      <Box display="flex" justifyContent="center" mt={3} mb={2}>
                        <Alert severity="warning" sx={{ width: "100%", maxWidth: 900 }}>
                          Existen tragamonedas con contadores pendientes de valores iniciales.
                          Las recaudaciones posteriores al inicio del ciclo requieren completarlos.
                        </Alert>
                      </Box>
                    )}
                    <Box display="flex" justifyContent="center" mt={3} gap={1.5} flexWrap="wrap">
                      <AddVisitTransactionButton
                        local={local}
                        profitPercentages={profitPercentages}
                        localMachineDetails={orderedLocalMachines}
                        disabled={hasPendingInitialSlotMachines}
                        returnTo={recaudacionesReturnTo}
                      />
                    </Box>
                  </>
                )}
              </MDBox>

              {/* accesos a las recaudaciones del local */}
              {local && localMachineDetails.length > 0 && (
                <>
                  {canViewLocalCollections && (
                    <MDBox mx={2} mt={3} display="flex" justifyContent="flex-end" gap={1.5} flexWrap="wrap">
                      <Button
                        variant="contained"
                        color="primary"
                        sx={{ color: "#fff" }}
                        onClick={() =>
                          navigate(`/local/${local.Id}/recaudaciones/tragamonedas`, { state: { local } })
                        }
                      >
                        Ver recaudaciones
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        sx={{ color: '#fff' }}
                        onClick={() =>
                          navigate(`/local/${local.Id}/recaudaciones`, { state: { local } })
                        }
                      >
                        {incomeButtonLabel}
                      </Button>
                    </MDBox>
                  )}
                </>
              )}
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <AppFormDialog
        open={isCreateModalOpen}
        onClose={handleCloseCreateModal}
        title={createMachineDialogTitle}
        disableEnforceFocus
        maxWidth="md"
        contentSx={{ display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden", pb: 0 }}
      >
          <CreateLocalMachineForm
            closeForm={handleCloseCreateModal}
            local={local}
            onCreateSuccess={async () => {
              if (!Number.isFinite(localId)) return;
              await refreshCollectionPrerequisites();
            }}
          />
      </AppFormDialog>

      <AppFormDialog
        open={isCounterConfigModalOpen}
        onClose={handleCloseCounterConfigModal}
        title={counterConfigModalTitle}
        disableEnforceFocus
        maxWidth="md"
        actions={
          <>
            <CancelButton onClick={handleCloseCounterConfigModal} disabled={isCounterConfigSaving}>
              Cancelar
            </CancelButton>
            <AcceptButton
              onClick={handleSaveCounterConfig}
              disabled={isCounterConfigSaving || isCounterConfigDateBoundsLoading}
            >
              {isCounterConfigSaving
                ? "Guardando..."
                : activeCounterConfigForModal
                  ? "Guardar cambios"
                  : "Configurar contadores"}
            </AcceptButton>
          </>
        }
      >
          <MDBox sx={{ pt: 0.5, minWidth: 0 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <AppFormContext
                  title={[getType(counterConfigMachine), buildMachineDisplayName(counterConfigMachine, { fallback: "Sin nombre" })].filter(Boolean).join(" ")}
                  lines={counterConfigContextLines}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(counterConfigForm.useDifferentFormulas)}
                      onChange={(event) =>
                        handleCounterUseDifferentFormulasChange(event.target.checked)
                      }
                    />
                  }
                  label="Usar formula distinta para entrada y salida"
                />
              </Grid>

              {!counterConfigForm.useDifferentFormulas && (
                <Grid item xs={12}>
                  {renderCounterFormulaFields("shared", "Formula de contador")}
                </Grid>
              )}

              {counterConfigForm.useDifferentFormulas && (
                <>
                  <Grid item xs={12} md={6}>
                    <MDTypography variant="subtitle2" sx={{ mb: 1 }}>
                      Entrada
                    </MDTypography>
                    {renderCounterFormulaFields("in", "Formula contador entrada")}
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <MDTypography variant="subtitle2" sx={{ mb: 1 }}>
                      Salida
                    </MDTypography>
                    {renderCounterFormulaFields("out", "Formula contador salida")}
                  </Grid>
                </>
              )}

              <Grid item xs={12} md={6}>
                <AppNumberInput
                  fullWidth
                  label="Tolerancia permitida ($)"
                  required
                  value={counterConfigForm.ToleranceAmount}
                  onValueChange={(value) =>
                    handleCounterConfigFormChange("ToleranceAmount", value)
                  }
                  decimalScale={2}
                  fixedDecimalScale={false}
                  allowNegative={false}
                  min={0}
                  error={counterToleranceFieldError}
                  helperText={counterToleranceFieldError ? "Ingresá una tolerancia mayor o igual a cero." : ""}
                />
              </Grid>

              <Grid item xs={12}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={esLocale}>
                  <DatePicker
                    label="Fecha efectiva del cambio"
                    value={counterConfigPickerDate}
                    onChange={(date) => {
                      setCounterConfigPickerDate(date);
                      handleCounterConfigFormChange("EffectiveDate", toLocalDateOnlyString(date));
                    }}
                    minDate={parseDateOnlyToLocalDate(counterConfigDateBounds?.MinEffectiveDate) || undefined}
                    maxDate={parseDateOnlyToLocalDate(counterConfigMaxEffectiveDate) || undefined}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        error: Boolean(counterConfigDateFieldError),
                        helperText: counterConfigDateFieldError || counterConfigDateHelperText,
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>

            </Grid>

            {counterConfigModalError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {counterConfigModalError}
              </Alert>
            )}

            {activeCounterConfigForModal && (
              <Box
                sx={{
                  mt: 3,
                  pt: 2,
                  borderTop: "1px solid",
                  borderColor: "divider",
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <Button
                  variant="outlined"
                  color="info"
                  sx={secondaryOutlinedButtonSx}
                  onClick={handleOpenRemoveCounterConfigConfirm}
                  disabled={isCounterConfigSaving || isCounterConfigDateBoundsLoading}
                >
                  Quitar contadores
                </Button>
              </Box>
            )}
          </MDBox>
      </AppFormDialog>

      <AppActionDialog
        open={isRemoveCounterConfigConfirmOpen}
        onClose={handleCloseRemoveCounterConfigConfirm}
        onConfirm={handleRemoveCounterConfig}
        title="Quitar contadores"
        description={`Desde el día ${formatDateOnlyForDisplay(counterConfigForm.EffectiveDate)} la máquina dejará de usar doble verificación. Las recaudaciones anteriores conservarán sus datos de contadores.`}
        confirmLabel={isRemovingCounterConfig ? "Quitando..." : "Quitar contadores"}
        confirmColor="error"
        busy={isRemovingCounterConfig}
        confirmDisabled={isCounterConfigDateBoundsLoading}
      >
        {removeCounterConfigError && <Alert severity="error">{removeCounterConfigError}</Alert>}
      </AppActionDialog>

      {localMachineToDelete && (
        <AppFormDialog open={isDeleteModalOpen} onClose={handleCloseDeleteModal} title={getDeleteDialogTitle(localMachineToDelete)}>
            <DeleteLocalMachineForm
              closeForm={handleCloseDeleteModal}
              localMachine={localMachineToDelete}
              onDeleteSuccess={handleDeleteSuccess}
            />
        </AppFormDialog>
      )}

      {localMachineToEdit && (
      <AppFormDialog open={isEditModalOpen} onClose={handleCloseEditModal} title={getEditDialogTitle(localMachineToEdit)} maxWidth="md" disableEnforceFocus>
          <EditLocalMachineForm
            local={local}
            localMachine={localMachineToEdit}
            closeForm={handleCloseEditModal}
            onUpdateSuccess={handleEditSuccess}
          />
      </AppFormDialog>
    )} 

    {localMachineToEditDisplayNumber && (
      <AppFormDialog
        open={isEditDisplayNumberModalOpen}
        onClose={handleCloseEditDisplayNumberModal}
        title="Editar Nº en local"
        disableEnforceFocus
      >
          <EditLocalDisplayNumberForm
            localMachine={localMachineToEditDisplayNumber}
            onClose={handleCloseEditDisplayNumberModal}
            onSuccess={handleEditDisplayNumberSuccess}
          />
      </AppFormDialog>
    )}

    {counterInitialValuesMachine && (
      <AppFormDialog
        open={isCounterInitialValuesModalOpen}
        onClose={handleCloseCounterInitialValuesModal}
        title="Carga de valores iniciales de contadores"
        disableEnforceFocus
        disableEscapeKeyDown={isCounterInitialValuesSaving}
      >
          <CounterInitialValuesForm
            localMachine={counterInitialValuesMachine}
            counterConfig={
              counterConfigsByLocalMachineId[
                Number(counterInitialValuesMachine?.Id ?? counterInitialValuesMachine?.LocalMachineId)
              ]
            }
            status={
              counterInitialValuesStatusByLocalMachineId[
                Number(counterInitialValuesMachine?.Id ?? counterInitialValuesMachine?.LocalMachineId)
              ]
            }
            closeForm={handleCloseCounterInitialValuesModal}
            onSuccess={handleCounterInitialValuesSuccess}
            onSavingChange={setIsCounterInitialValuesSaving}
          />
      </AppFormDialog>
    )}

    {localMachineForInitialValues && (
      <AppFormDialog
        open={isInitialValuesModalOpen}
        onClose={handleCloseInitialValuesModal}
        title="Carga de valores iniciales"
        maxWidth="md"
        disableEnforceFocus
        disableEscapeKeyDown={isInitialValuesSaving}
      >
          <InitialValuesPerMachineForm
            localMachine={localMachineForInitialValues}
            closeForm={handleCloseInitialValuesModal}
            onSuccess={handleInitialValuesSuccess}
            onSavingChange={setIsInitialValuesSaving}
            onStateRefreshRequested={handleInitialValuesStateRefresh}
          />
      </AppFormDialog>
    )}

    {local && (
      <AppFormDialog
        open={isEditFormulasModalOpen}
        onClose={handleCloseEditFormulasModal}
        title="Editar fórmulas por tipo"
        maxWidth="md"
        disableEnforceFocus
      >
          <EditFormulasByTypeForm
            localId={local.Id}
            localMachineDetails={localMachineDetails}
            onClose={handleCloseEditFormulasModal}
            onSuccess={handleEditFormulasSuccess}
            getType={getType}
            isFichaType={isFichaType}
            getFormulaName={getFormulaName}
            getFormulaValue={getFormulaValue}
            money={money}
          />
      </AppFormDialog>
    )}

    {local && (
      <AppFormDialog
        open={isEditProfitModalOpen}
        onClose={handleCloseEditProfitModal}
        title="Editar porcentajes cliente/dueño"
        maxWidth="md"
        disableEnforceFocus
      >
          <EditProfitPercentagesForm
            localId={local.Id}
            localMachineDetails={localMachineDetails}
            profitPercentages={profitPercentages}
            onClose={handleCloseEditProfitModal}
            onSuccess={handleEditProfitSuccess}
          />
      </AppFormDialog>
    )}

        {local && (
      <AppFormDialog
        open={isBulkMoveModalOpen}
        onClose={handleCloseBulkMoveModal}
        title="Cerrar local"
        maxWidth="md"
        disableEnforceFocus
      >
          <BulkMoveLocalMachinesForm
            local={local}
            localMachineDetails={localMachineDetails}
            onClose={handleCloseBulkMoveModal}
            onSuccess={handleBulkMoveSuccess}
          />
      </AppFormDialog>
    )}

      <AppSnackbar
        open={snack.open}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        severity={snack.severity}
        message={snack.message}
      />

      <Footer />
    </DashboardLayout>
  );
}

export default LocalMachine;




